import React from 'react';
import { Link } from 'react-router-dom';
import {
  ShoppingCart, Award, CreditCard, Star, Truck, Bell, Heart, RotateCcw,
  Package, Users, Shield, Zap, ArrowRight, Fish, TrendingUp, Gift,
  MessageCircle, Search, Clock, MapPin, Smartphone
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './FeaturesPage.css';

const features = [
  {
    icon: ShoppingCart,
    title: 'Easy Online Shopping',
    desc: 'Browse our full catalogue of fresh seafood, filter by category, and add to cart in seconds. Shop anytime, anywhere.',
    color: '#29ABE2',
  },
  {
    icon: Truck,
    title: 'Fast, Reliable Delivery',
    desc: 'Free delivery in Greater Accra. Track your order in real time from confirmation to doorstep.',
    color: '#1565C0',
  },
  {
    icon: Award,
    title: 'Loyalty Points System',
    desc: 'Earn 1 Aurora Point for every GHS 1 spent. Redeem 100 points for GHS 1 off your next order.',
    color: '#F59E0B',
  },
  {
    icon: TrendingUp,
    title: 'Tier Progression',
    desc: 'Rise through Shell, Silverfin, Goldfin, and Blue Marlin tiers. Each level unlocks better rewards and perks.',
    color: '#10B981',
  },
  {
    icon: CreditCard,
    title: 'Loyalty Cards',
    desc: 'Purchase a loyalty card and enjoy fixed discounts (5%–20%) on every single order for its full validity period.',
    color: '#8B5CF6',
  },
  {
    icon: Bell,
    title: 'Real-Time Notifications',
    desc: 'Stay informed with instant notifications for order updates, delivery status, loyalty rewards, and exclusive promotions.',
    color: '#EF4444',
  },
  {
    icon: Heart,
    title: 'Wishlist',
    desc: 'Save your favourite products to your wishlist and come back to them anytime. Never lose track of what you love.',
    color: '#EC4899',
  },
  {
    icon: Star,
    title: 'Product Reviews',
    desc: 'Share your experience and read honest reviews from other customers to make confident buying decisions.',
    color: '#F59E0B',
  },
  {
    icon: RotateCcw,
    title: 'Order History & Tracking',
    desc: 'View your complete order history, download invoices, and track every delivery from your personal dashboard.',
    color: '#06B6D4',
  },
  {
    icon: Gift,
    title: 'Coupons & Discounts',
    desc: 'Apply exclusive coupon codes at checkout for instant savings. Members receive special promo codes regularly.',
    color: '#10B981',
  },
  {
    icon: Shield,
    title: 'Secure Payments',
    desc: 'Pay safely with Paystack, MTN Mobile Money, Telecel Cash, or Airtel Money. Your financial data is always protected.',
    color: '#1565C0',
  },
  {
    icon: MessageCircle,
    title: 'Live Support Chat',
    desc: 'Need help? Chat directly with our Aurora support team in real time from your dashboard.',
    color: '#29ABE2',
  },
  {
    icon: Users,
    title: 'Referral & Reseller Program',
    desc: 'Invite friends and earn referral rewards. Qualified members can become resellers and earn commissions on sales.',
    color: '#8B5CF6',
  },
  {
    icon: Search,
    title: 'Smart Product Search',
    desc: 'Find exactly what you\'re looking for instantly with our powerful search and filter system by category, price, and availability.',
    color: '#F59E0B',
  },
  {
    icon: Smartphone,
    title: 'Mobile Friendly',
    desc: 'Aurora works perfectly on all devices. Shop, manage your account, and track orders from your phone with ease.',
    color: '#10B981',
  },
  {
    icon: MapPin,
    title: 'Multiple Delivery Addresses',
    desc: 'Save multiple delivery addresses to your account for quick checkout — home, office, or any location in Ghana.',
    color: '#EF4444',
  },
];

const tiers = [
  { name: 'Shell', class: 'Economy', xp: '0 XP', color: '#94a3b8', perks: ['Earn Aurora Points', 'Access to all products', 'Order tracking'] },
  { name: 'Silverfin', class: 'Business', xp: '50 XP', color: '#29ABE2', perks: ['All Shell benefits', 'Higher points multiplier', 'Priority support'] },
  { name: 'Goldfin', class: 'First', xp: '150 XP', color: '#F59E0B', perks: ['All Silverfin benefits', 'Exclusive deals', 'Early access to products'] },
  { name: 'Blue Marlin', class: 'Prime', xp: '300 XP', color: '#8B5CF6', perks: ['All Goldfin benefits', 'Maximum rewards', 'VIP member status'] },
];

const FeaturesPage = () => {
  return (
    <>
      <Navbar />
      <div className="features-page">

        {/* Hero */}
        <section className="fp-hero">
          <div className="container">
            <div className="fp-hero-badge"><Zap size={14} /> Everything You Get as an Aurora Member</div>
            <h1 className="fp-hero-title">
              Shop Smarter.<br />
              <span>Earn More.</span> Live Fresh.
            </h1>
            <p className="fp-hero-sub">
              Aurora isn't just a seafood store — it's a full loyalty experience. From points to tiers to live chat support, here's everything built for you.
            </p>
            <div className="fp-hero-actions">
              <Link to="/register" className="hero-btn-primary">
                Create Free Account <ArrowRight size={18} />
              </Link>
              <Link to="/shop" className="hero-btn-secondary">
                Browse Products
              </Link>
            </div>
            <div className="fp-hero-stats">
              <div className="fp-stat"><span>16+</span><p>Customer Features</p></div>
              <div className="fp-stat-div" />
              <div className="fp-stat"><span>4</span><p>Loyalty Tiers</p></div>
              <div className="fp-stat-div" />
              <div className="fp-stat"><span>100%</span><p>Free to Join</p></div>
            </div>
          </div>
        </section>

        {/* All Features Grid */}
        <section className="fp-features-section">
          <div className="container">
            <div className="fp-section-header">
              <h2>Everything Designed <span>For You</span></h2>
              <p>Every feature on Aurora is built around making your seafood shopping experience better, easier, and more rewarding.</p>
            </div>
            <div className="fp-features-grid">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="fp-feature-card" style={{ '--feat-color': f.color }}>
                    <div className="fp-feature-icon">
                      <Icon size={22} color={f.color} />
                    </div>
                    <h3>{f.title}</h3>
                    <p>{f.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Loyalty Deep Dive */}
        <section className="fp-loyalty-section">
          <div className="container">
            <div className="fp-section-header light">
              <div className="fp-section-badge"><Award size={14} /> Aurora Loyalty Program</div>
              <h2>The More You Shop, <span>The More You Earn</span></h2>
              <p>Our loyalty system is designed to reward you at every step. Every purchase brings you closer to the next tier and bigger benefits.</p>
            </div>

            <div className="fp-loyalty-how">
              <div className="fp-loyalty-step">
                <div className="fp-loyalty-step-num">1</div>
                <h4>Shop & Earn</h4>
                <p>GHS 1 spent = 1 Aurora Point automatically added to your wallet</p>
              </div>
              <div className="fp-loyalty-arrow"><ArrowRight size={20} /></div>
              <div className="fp-loyalty-step">
                <div className="fp-loyalty-step-num">2</div>
                <h4>Gain XP</h4>
                <p>Each purchase earns Status XP to progress through loyalty tiers</p>
              </div>
              <div className="fp-loyalty-arrow"><ArrowRight size={20} /></div>
              <div className="fp-loyalty-step">
                <div className="fp-loyalty-step-num">3</div>
                <h4>Climb Tiers</h4>
                <p>Unlock better rewards, multipliers, and perks as you level up</p>
              </div>
              <div className="fp-loyalty-arrow"><ArrowRight size={20} /></div>
              <div className="fp-loyalty-step">
                <div className="fp-loyalty-step-num">4</div>
                <h4>Redeem</h4>
                <p>100 Points = GHS 1 — use points at checkout for instant discounts</p>
              </div>
            </div>

            <div className="fp-tiers-grid">
              {tiers.map((tier, i) => (
                <div key={i} className="fp-tier-card" style={{ '--tier-color': tier.color }}>
                  <div className="fp-tier-fish">
                    <Fish size={36} color={tier.color} strokeWidth={1.5} />
                  </div>
                  <div className="fp-tier-name" style={{ color: tier.color }}>{tier.name}</div>
                  <div className="fp-tier-class">{tier.class} Class</div>
                  <div className="fp-tier-xp">{tier.xp} required</div>
                  <ul className="fp-tier-perks">
                    {tier.perks.map((p, j) => (
                      <li key={j}><span className="fp-tier-dot" style={{ background: tier.color }} />{p}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Points System Explainer */}
        <section className="fp-points-section">
          <div className="container">
            <div className="fp-points-layout">
              <div className="fp-points-left">
                <div className="fp-section-badge"><TrendingUp size={14} /> Aurora Points</div>
                <h2>Your Points, <span>Your Savings</span></h2>
                <p>Aurora Points are your digital currency. Earn them automatically on every purchase and redeem them for real discounts.</p>
                <div className="fp-points-examples">
                  <div className="fp-point-example">
                    <div className="fp-point-icon"><ShoppingCart size={18} color="#29ABE2" /></div>
                    <div>
                      <strong>Order GHS 200</strong>
                      <span>Earn 200 Aurora Points</span>
                    </div>
                  </div>
                  <div className="fp-point-example">
                    <div className="fp-point-icon"><Gift size={18} color="#10B981" /></div>
                    <div>
                      <strong>500 Points</strong>
                      <span>= GHS 5 discount on next order</span>
                    </div>
                  </div>
                  <div className="fp-point-example">
                    <div className="fp-point-icon"><CreditCard size={18} color="#8B5CF6" /></div>
                    <div>
                      <strong>Loyalty Card</strong>
                      <span>GHS 200 card = 20% off all orders</span>
                    </div>
                  </div>
                </div>
                <Link to="/register" className="hero-btn-primary" style={{ display: 'inline-flex', marginTop: 8 }}>
                  Start Earning Today <ArrowRight size={18} />
                </Link>
              </div>
              <div className="fp-points-right">
                <div className="fp-wallet-preview">
                  <div className="fp-wallet-header">
                    <Fish size={20} color="#29ABE2" strokeWidth={1.5} />
                    <span>My Aurora Wallet</span>
                  </div>
                  <div className="fp-wallet-balance">
                    <span className="fp-wallet-points">1,250</span>
                    <span className="fp-wallet-label">Aurora Points</span>
                  </div>
                  <div className="fp-wallet-worth">Worth GHS 12.50</div>
                  <div className="fp-wallet-tier">
                    <div className="fp-wallet-tier-dot" style={{ background: '#29ABE2' }} />
                    Silverfin Member · 85/150 XP
                  </div>
                  <div className="fp-wallet-progress">
                    <div className="fp-wallet-progress-bar" style={{ width: '56%', background: '#29ABE2' }} />
                  </div>
                  <p className="fp-wallet-next">65 XP to reach Goldfin</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="fp-cta">
          <div className="container">
            <div className="fp-cta-inner">
              <Fish size={48} color="rgba(255,255,255,0.15)" strokeWidth={1} />
              <h2>Ready to Experience Aurora?</h2>
              <p>Join thousands of customers already earning rewards and enjoying fresh seafood delivered to their door.</p>
              <div className="fp-cta-actions">
                <Link to="/register" className="fp-cta-btn-primary">Create Free Account <ArrowRight size={18} /></Link>
                <Link to="/shop" className="fp-cta-btn-secondary">Browse Products</Link>
              </div>
            </div>
          </div>
        </section>

      </div>
      <Footer />
    </>
  );
};

export default FeaturesPage;